import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { programsApi } from 'api';
import { useOrganizationPanel } from 'contexts/organizationPanelContext';
import { Toast } from 'primereact/toast';
import { Program } from 'types/Program';
import { Dialog } from 'components/_new/Dialog';
import { InputText } from 'components/_new/InputText';
import { Button } from 'components/_new/Button';
import { clearInput } from 'utils/clearInput';
import CustomConfirmDialog from '../_common/ConfirmDialog';

type NewProgramFormData = {
	displayName: string;
	name: string;
	subtitle?: string;
	logotype?: File;
};

interface NewProgramDialogProps {
	visible: boolean;
	onHide: () => void;
	onSuccess?: () => void;
}

const slugify = (text: string, organizationName: string): string => {
	const slug = text
		.toLowerCase()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/(^-|-$)/g, '');
	return `${organizationName}/${slug}`;
};

export const NewProgramDialog: React.FC<NewProgramDialogProps> = ({ visible, onHide, onSuccess }) => {
	const { t } = useTranslation();
	const toast = useRef<Toast>(null);
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const {
		currentOrganization: { organizationName },
	} = useOrganizationPanel();
	const inputFileRef = useRef<HTMLInputElement>(null);
	const [previewImage, setPreviewImage] = useState<string | null>(null);

	const form = useForm<NewProgramFormData>({
		defaultValues: {
			displayName: '',
			name: '',
			subtitle: '',
			logotype: undefined,
		},
		mode: 'onChange',
	});

	useEffect(() => {
		if (form.watch('displayName') && organizationName) {
			const timeoutId = setTimeout(() => {
				form.setValue('name', slugify(form.watch('displayName'), organizationName));
			}, 300);
			return () => clearTimeout(timeoutId);
		}
		return undefined;
	}, [form.watch('displayName'), form.setValue, organizationName]);

	const createProgram = useMutation<Program, Error, NewProgramFormData>(
		async (data) => {
			const formData = new FormData();
			formData.append('displayName', data.displayName);
			formData.append('name', data.name);
			formData.append('language', 'pl');
			formData.append('authRequired', 'false');
			formData.append('programType', 'classic');
			formData.append('finished', 'true');

			if (data.logotype instanceof File) {
				formData.append('logotype', data.logotype);
			}
			return programsApi.createForOrganization(organizationName!, formData);
		},
		{
			onSuccess: () => {
				toast.current?.show({
					severity: 'success',
					summary: t('organizationPanel.program.created'),
					life: 3000,
				});
				onSuccess?.();
				onHide();
			},
			onError: (error: any) => {
				const message = error.response?.data?.message;
				const errorKey = message === 'Program name already exists' ? 'nameExists' : 'default';

				toast.current?.show({
					severity: 'error',
					summary: t(`organizationPanel.program.error.${errorKey}`),
					life: 3000,
				});
			},
		}
	);

	const handleConfirm = form.handleSubmit((data: NewProgramFormData) => {
		if (!/^[a-z0-9-/]+$/.test(data.name)) {
			toast.current?.show({
				severity: 'error',
				summary: t('organizationPanel.program.error.invalidCharacters'),
				life: 3000,
			});
			return;
		}
		createProgram.mutate(data);
	});

	return (
		<>
			<Toast ref={toast} />
			<CustomConfirmDialog
				confirmationOpen={confirmationOpen}
				setConfirmationOpen={setConfirmationOpen}
				handleAccept={() => {
					setConfirmationOpen(false);
					handleConfirm();
				}}
				header={t('programPanel.dashboard.status.confirmHeader')}
				message={t('programPanel.dashboard.status.confirmMessage')}
			/>
			<Dialog visible={visible} onHide={onHide} title={t('programPanel.dashboard.status.newTitle')} size="md">
				<form onSubmit={handleConfirm}>
					<p>{t('programPanel.dashboard.status.newDescription')}</p>

					<div className="flex flex-column gap-3">
						<div className="flex flex-row align-items-center">
							<label className="w-3">{t('programPanel.dashboard.status.programName')}</label>
							<Controller
								control={form.control}
								name="displayName"
								render={({ field }) => (
									<InputText {...field} maxLength={60} disabled={createProgram.isLoading} />
								)}
							/>
						</div>

						<div className="flex flex-row align-items-center">
							<label className="w-3">{t('programPanel.dashboard.status.programUrlAddress')}</label>
							<Controller
								control={form.control}
								name="name"
								render={({ field }) => <InputText {...field} disabled={createProgram.isLoading} />}
							/>
						</div>

						<div className="flex flex-row align-items-center">
							<label className="w-3">{t('programPanel.dashboard.status.programSubtitle')}</label>
							<Controller
								control={form.control}
								name="subtitle"
								render={({ field }) => <InputText {...field} disabled={createProgram.isLoading} />}
							/>
						</div>

						<div className="flex flex-row align-items-center">
							<label className="w-3">{t('programPanel.dashboard.status.programLogotype')}</label>
							<Controller
								control={form.control}
								name="logotype"
								render={({ field }) => (
									<div className=" gap-2 w-full">
										<InputText
											ref={inputFileRef}
											type="file"
											accept="image/jpeg,image/png,image/gif,image/bmp,image/svg+xml"
											onChange={(e) => {
												const file = e.currentTarget.files?.[0];
												if (file) {
													setPreviewImage(URL.createObjectURL(file));
													field.onChange(file);
												}
											}}
										/>
										{previewImage && (
											<div className="flex flex-row mt-3">
												<Button
													label={t('actions.delete')}
													onClick={() => {
														if (inputFileRef.current) clearInput(inputFileRef.current);
														setPreviewImage(null);
													}}
													icon="trash"
													iconOnly
													variant="danger-text"
													variantSize="sm"
												/>
												<img
													src={previewImage}
													className="upload-preview-image logotype"
													alt="preview"
												/>
											</div>
										)}
									</div>
								)}
							/>
						</div>
					</div>

					<div className="flex justify-content-end gap-2 mt-4">
						<Button label={t('actions.cancel')} onClick={onHide} variant="primary-outlined" />
						<Button
							label={t('actions.save')}
							onClick={() => setConfirmationOpen(true)}
							loading={createProgram.isLoading}
						/>
					</div>
				</form>
			</Dialog>
		</>
	);
};
